import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Usergroup, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const usergroupCreateSchema = z.object({
  name: z.string().min(1, '部署名は必須です'),
  branch: z.string().default(''),
  section: z.string().default(''),
  shortName: z.string().default(''),
  areaId: z.string().nullable().default(null),
  tel: z.string().default(''),
  fax: z.string().default(''),
  email: z.string().email('メールアドレスの形式が正しくありません').or(z.literal('')).default(''),
  zip: z.string().default(''),
  prefecture: z.string().default(''),
  addr1: z.string().default(''),
  addr2: z.string().default(''),
  addr3: z.string().default(''),
  note: z.string().default(''),
  stampUrl: z.union([
    z.string().url().refine((v) => v.startsWith('https://'), {
      message: 'stampUrl は https:// スキームのみ許可されています。',
    }),
    z.null(),
  ]).default(null),
  icon: z.union([
    z.string().url().refine((v) => v.startsWith('https://'), {
      message: 'icon は https:// スキームのみ許可されています。',
    }),
    z.null(),
  ]).default(null),
  priority: z.number().default(0),
});

export type UsergroupCreateInput = z.infer<typeof usergroupCreateSchema>;
export type UsergroupUpdateInput = Partial<UsergroupCreateInput>;

const COL = 'usergroups';

export async function fetchUsergroups(): Promise<Usergroup[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('priority')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Usergroup);
}

export async function fetchUsergroup(id: ID): Promise<Usergroup | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<Usergroup>(snap);
}

export async function createUsergroup(
  data: UsergroupCreateInput,
  userId: ID
): Promise<Usergroup> {
  const validated = parseOrThrow(usergroupCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as Usergroup;
  await writeAuditLog({
    entityType: 'usergroup',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateUsergroup(
  id: ID,
  data: UsergroupUpdateInput,
  userId: ID
): Promise<Usergroup> {
  const validated = usergroupCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('部署が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'usergroup',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as Usergroup;
}

export async function deleteUsergroup(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('部署が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'usergroup',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}

export async function updateUsergroupStamp(
  id: ID,
  stampUrl: string | null,
  userId: ID
): Promise<void> {
  if (stampUrl !== null && !stampUrl.startsWith('https://')) {
    throw new Error('stampUrl は https:// スキームのみ許可されています。');
  }
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('部署が見つかりません。');
  const before = snap.data();
  await updateDoc(ref, { stampUrl, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'usergroup',
    entityId: id,
    action: 'update',
    userId,
    before: { stampUrl: before['stampUrl'] },
    after: { stampUrl },
  });
}
